import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Tag, Tooltip, Progress, Badge, Empty, Spin, Typography, Button } from 'antd';
import { Workflow, Cpu, Scale, PlayCircle, StopCircle } from 'lucide-react';
import Icons from '@/components/third-party/Icons';
import TableContainer from '@/components/TableContainer';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import { get_batch_process_list } from '@/utils/api';
import { resolveOperationExecutionStatus } from '@/utils/operationExecutionStatus';
import { fmt, yesNo, statusTagColor } from './helpers';
import ProcessOperationsModal from './ProcessOperationsModal';

const { Title } = Typography;

// ==============================|| BATCH PROCESS DETAILS ||============================== //

const BatchProcessDetails = () => {
  const { batch_number } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const axisColor = isDark ? '#94a3b8' : '#64748b';

  const [batchData, setBatchData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState(null);

  const fetchBatchDetails = () => {
    setLoading(true);
    get_batch_process_list({ batch_number }, (res) => {
      setBatchData(res?.data || null);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (batch_number) fetchBatchDetails();
  }, [batch_number]);

  const processes = batchData?.processes || [];
  const totalProcesses = processes.length;

  const columns = [
    { title: 'Process Code', dataIndex: 'process_code', key: 'process_code', width: 110, render: (v) => v || '—' },
    {
      title: 'Process Name',
      dataIndex: 'process_name',
      key: 'process_name',
      width: 200,
      render: (v) => (
        <Tooltip title={v}>
          <div className="truncate" style={{ maxWidth: 190 }}>{v || '—'}</div>
        </Tooltip>
      ),
    },
    { title: 'Duration', dataIndex: 'duration', key: 'duration', width: 90, render: (v) => (v != null ? `${v} min` : '—') },
    {
      title: 'Dispensing',
      dataIndex: 'is_dispensing_process',
      key: 'is_dispensing_process',
      width: 100,
      align: 'center',
      render: yesNo,
    },
    {
      title: 'Execution Status',
      dataIndex: 'execution_status',
      key: 'execution_status',
      width: 150,
      render: (v) => {
        const resolved = resolveOperationExecutionStatus(v);
        return <Tag color={resolved.style.tagColor}>{resolved.label}</Tag>;
      },
    },
    {
      title: 'Schedule',
      key: 'schedule',
      width: 200,
      render: (_, r) => {
        const start = r.schedule?.start_dt;
        const end = r.schedule?.end_dt;
        const scheduledFor = r.schedule?.schedule_dt;
        if (start || end) {
          return (
            <Tooltip title={scheduledFor ? `Scheduled for ${fmt(scheduledFor)}` : undefined}>
              <div style={{ fontSize: 12 }}>
                {fmt(start)} → {fmt(end)}
              </div>
            </Tooltip>
          );
        }
        if (scheduledFor) {
          return <Tag color="blue">Scheduled for {fmt(scheduledFor)}</Tag>;
        }
        return <Tag color="default">Not Scheduled</Tag>;
      },
    },
    {
      title: 'MBMR Approval',
      dataIndex: 'recipe_approval_status',
      key: 'recipe_approval_status',
      width: 140,
      render: (v) => (v ? <Tag color={statusTagColor(v)}>{v}</Tag> : '—'),
    },
    {
      title: 'Batch Approval',
      key: 'batch_approval',
      width: 130,
      render: (_, r) =>
        r.batch_approval?.status ? (
          <Tag color={statusTagColor(r.batch_approval.status)}>{r.batch_approval.status}</Tag>
        ) : (
          '—'
        ),
    },
    {
      title: 'Operations',
      key: 'operations_summary',
      width: 160,
      render: (_, r) => {
        const total = r.operations_summary?.total || 0;
        const completed = r.operations_summary?.completed || 0;
        const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
        return (
          <div style={{ minWidth: 130 }}>
            <Progress percent={pct} size="small" status={pct === 100 ? 'success' : 'active'} />
            <div style={{ fontSize: 11, color: axisColor }}>
              {completed}/{total} completed
              {r.operations_summary?.pending ? ` · ${r.operations_summary.pending} pending` : ''}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Deviations',
      dataIndex: 'deviations_total',
      key: 'deviations_total',
      width: 100,
      align: 'center',
      render: (v) => <Badge count={v || 0} showZero color={v ? '#dc2626' : '#94a3b8'} />,
    },
    {
      title: 'Details',
      key: 'action',
      width: 90,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Tooltip title="View Operations">
          <span className="action-icon flex items-center justify-center w-full">
            <Icons.Eye
              className="w-5 h-5 cursor-pointer text-blue-600 hover:text-blue-800"
              onClick={() => setSelectedProcess(record)}
            />
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <div
      className="p-2.5 pt-5"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
    <Card
      className="border border-gray-300 shadow-md"
      bordered={false}
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff', minHeight: 'calc(100vh - 120px)' }}
      styles={{ body: { padding: 10 } }}
    >
      {loading ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin size="large" tip="Loading batch details…" />
        </div>
      ) : !batchData ? (
        <Empty description="No details found for this batch" style={{ padding: 48 }} />
      ) : (
        <>
          {/* Hero header */}
          <Card
            size="small"
            style={{ marginBottom: 16, overflow: 'hidden' }}
            styles={{ body: { padding: 0 } }}
          >
            <div
              style={{
                background: isDark ? '#0F172A' : '#204448',
                padding: '10px 20px',
                color: '#fff',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <Icons.ClipboardList size={18} />
                  <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: 0.2 }}>
                    {batchData.batch_number}
                  </span>
                  <Tag color={statusTagColor(batchData.batch_status)} style={{ fontWeight: 600 }}>
                    {batchData.batch_status || 'Unknown'}
                  </Tag>
                </div>
                <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 2 }}>
                  {batchData.recipe?.name || '—'}
                </div>
              </div>
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(-1);
                }}
                icon={<Icons.ArrowLeft className="w-4 h-4" />}
                style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', background: 'transparent' }}
              >
                Back
              </Button>
            </div>

            <Row gutter={0}>
              {[
                { label: 'MBMR', value: batchData.recipe?.name || '—', icon: <Icons.FlaskConical size={16} />, color: '#2563eb' },
                { label: 'Batch Size', value: batchData.batch_size ?? '—', icon: <Scale size={16} />, color: '#d97706' },
                { label: 'Status', value: batchData.batch_status || '—', icon: <Cpu size={16} />, color: '#0891b2' },
                { label: 'Start', value: fmt(batchData.start_datetime), icon: <PlayCircle size={16} />, color: '#16a34a' },
                { label: 'End', value: fmt(batchData.end_datetime), icon: <StopCircle size={16} />, color: batchData.end_datetime ? '#dc2626' : '#94a3b8' },
              ].map((f, i, arr) => (
                <Col xs={12} sm={8} md={6} lg={4} key={f.label}>
                  <div
                    style={{
                      padding: '8px 10px',
                      borderRight: i + 1 === arr.length ? 'none' : '1px solid #f0f0f0',
                      borderBottom: '1px solid #f0f0f0',
                      height: '100%',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: f.color, marginBottom: 4 }}>
                      {f.icon}
                      <span style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: axisColor }}>
                        {f.label}
                      </span>
                    </div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: textColor, wordBreak: 'break-word' }}>
                      {f.value}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>

          {/* Process table */}
          <Card
            size="small"
            title={
              <div className="flex items-center gap-2">
                <Workflow size={16} className="text-blue-600" />
                <Title level={5} style={{ margin: 0, color: textColor }}>
                  Processes ({totalProcesses})
                </Title>
              </div>
            }
            styles={{ body: { padding: 8 } }}
          >
            <TableContainer
              size="small"
              bordered
              data={processes.map((p, i) => ({ ...p, key: p.process_id ?? i }))}
              columns={columns}
              // TableContainer reserves ~56px below the rows box for the pagination bar
              // automatically when pagination is enabled — pass the FULL height here, not
              // the pre-reduced 466px value, or the reservation would be applied twice.
              height="calc(100vh - 390px)"
              pagination={{
                pageSize: 15,
                pageSizeOptions: ['10', '15', '20', '50', '100'],
              }}
              scrollX="max-content"
              locale={{ emptyText: <Empty description="No processes found for this batch" /> }}
            />
          </Card>
        </>
      )}

      <ProcessOperationsModal
        open={!!selectedProcess}
        onClose={() => setSelectedProcess(null)}
        batchNumber={batch_number}
        process={selectedProcess}
      />
    </Card>
    </div>
  );
};

export default BatchProcessDetails;
